import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import core, { App } from '@gin-betting/core';
import { RecordOf } from 'immutable';
import { includes } from 'lodash';
import { createStructuredSelector } from 'reselect';

import { useMemoSelector } from 'hooks';
import ui from 'modules/ui';
import type { StateShape } from 'types';

const {
    selectors: { selectOddFormat, selectOddsStatisticView },
} = ui;
const {
    entities: {
        selectors: {
            selectStatisticPercentsByMarket,
            selectStatisticPercentsByMatch,
        },
    },
    utils: { getIdsFromOddId },
} = core;

const {
    constants: { BETSLIP_ODD_STATUSES_TO_PLACE_BET },
    actions: { deleteBetslipOdd, addBetslipOdd },
    selectors: { selectOddButtonView },
    utils: { getBetslipOddId },
} = betslip;

interface Input {
    oddId: string;
}

interface Output {
    oddFormat: App.OddFormat;
    statisticPercent: number | null;
    isShowStatistic: boolean;
    handleToggleOdd: () => void;
    betslipOddId: string;
    isSelected: boolean;
    isActive: boolean;
}

const selectProps = createStructuredSelector({
    oddButtonView: selectOddButtonView,
    oddFormat: selectOddFormat,
    oddsStatisticView: selectOddsStatisticView,
});

const makeStatisticSelector = (statisticView: App.OddStatisticView) =>
    statisticView === App.OddStatisticView.MATCH
        ? selectStatisticPercentsByMatch
        : selectStatisticPercentsByMarket;

const useOddButton = ({ oddId }: Input): Output => {
    const dispatch = useDispatch();

    const {
        oddButtonView: { isSelected, betslipBetId, isActive, status },
        oddFormat,
        oddsStatisticView: { statisticView, show },
    } = useMemoSelector(selectProps, { oddId });

    const { marketId } = getIdsFromOddId(oddId) || {};

    const selectStatistic = useMemo(
        () => makeStatisticSelector(statisticView),
        [statisticView]
    );

    const statisticPercent = useSelector<
        RecordOf<StateShape>,
        ReturnType<typeof selectStatistic>
    >(state => selectStatistic(state, { marketId, oddId }));

    const isEditableBetslip = includes(
        BETSLIP_ODD_STATUSES_TO_PLACE_BET,
        status
    );

    const recipientBetslipBetId = isEditableBetslip ? betslipBetId : undefined;

    // NOTE: Uncomment to enable multi betslip!
    // const recipientBetslipBetId =
    //     isSingle || isEditableBetslip ? betslipBetId : initialBetslipBetId;

    const handleDeleteBetslipOdd = useCallback(() => {
        if (!isEditableBetslip) return;

        dispatch(deleteBetslipOdd({ oddId, betslipBetId }));
    }, [dispatch, betslipBetId, isEditableBetslip, oddId]);

    const handleAddBetslipOdd = useCallback(() => {
        if (!recipientBetslipBetId) return;

        dispatch(
            addBetslipOdd({
                oddId,
                betslipBetId: recipientBetslipBetId,
            })
        );
    }, [dispatch, recipientBetslipBetId, oddId]);

    const handleToggleOdd = useCallback(
        () => (isSelected ? handleDeleteBetslipOdd() : handleAddBetslipOdd()),
        [isSelected, handleAddBetslipOdd, handleDeleteBetslipOdd]
    );

    return {
        isActive,
        isSelected,
        betslipOddId: getBetslipOddId({ oddId, betslipBetId }),
        oddFormat,
        statisticPercent,
        isShowStatistic: show,
        handleToggleOdd,
    };
};

export default useOddButton;
